import { useEffect } from 'react'
import AlertComponent from '../../../../components/stories/molecules/alerts'
import {
  alertAlignVariantOptions,
  alertOrientationVariantOptions
} from '../../../../components/stories/molecules/alerts/constants'
import { useIntl } from 'react-intl'
import TextInputComponent from '../../../../components/stories/molecules/text-input'
import { textInputHintVariantOptions } from '../../../../components/stories/molecules/text-input/constants'
import { useLoginResetPasswordMutation } from '../../../../react-query/authentication/mutation/use-login-reset-password-mutation'
import { useSnackbar } from '../../../../components/stories/molecules/snackbar/component/snackbar-hook'
import { snackbarVariantOptions } from '../../../../components/stories/molecules/snackbar/constants'
import {
  buttonSizeOptions,
  buttonVariantOptions
} from '../../../../components/stories/atoms/button/constants'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { createSchemas } from '../login/form-schemas.js'
import { trackFirebaseEvent } from '../../../../firebase/firebase-analytics.jsx'

const ResetPassword = ({ email: emailProp = '', isOpen, onClose }) => {
  const { formatMessage } = useIntl()
  const { addSnackbar } = useSnackbar()

  const { isSuccess, mutateAsync, error, isPending } =
    useLoginResetPasswordMutation()

  // Create schemas with internationalization
  const { resetPasswordFormSchema } = createSchemas(formatMessage)

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    watch
  } = useForm({
    defaultValues: {
      email: emailProp
    },
    resolver: zodResolver(resetPasswordFormSchema),
    mode: 'onTouched'
  })

  // Get form values for validation
  const email = watch('email')

  const onSubmit = async (data) => {
    if (isValid) {
      await mutateAsync({ email: data.email })
    }
  }

  useEffect(() => {
    setValue('email', emailProp)
  }, [emailProp, setValue])

  useEffect(() => {
    if (error) {
      addSnackbar({
        message: formatMessage({ id: 'error_reset_password' }),
        snackbarType: snackbarVariantOptions.ERROR,
        hideCloseButton: true
      })
    }
  }, [error])

  useEffect(() => {
    if (isSuccess) {
      addSnackbar({
        message: formatMessage({ id: 'success_reset_password' }),
        snackbarType: snackbarVariantOptions.SUCCESS,
        hideCloseButton: true
      })
      onClose()
    }
  }, [isSuccess])

  useEffect(() => {
    trackFirebaseEvent('login_password_reset_started', {
      source: 'login'
    })
  }, [])

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <AlertComponent
        activeOverlay={true}
        isOpen={isOpen}
        title={formatMessage({ id: 'login_page_resetPassword' })}
        description={formatMessage({ id: 'reset_password_instruction' })}
        buttonOrientation={alertOrientationVariantOptions.STACKED}
        buttonAlignment={alertAlignVariantOptions.END}
        actionButtons={[
          {
            variant: buttonVariantOptions['TERTIARY-HOLLOW'],
            size: buttonSizeOptions.LARGE,
            onClick: onClose,
            type: 'button',
            children: formatMessage({ id: 'cancel' }),
            fullWidth: true
          },
          {
            variant: buttonVariantOptions.PRIMARY,
            size: buttonSizeOptions.LARGE,
            type: 'submit',
            children: formatMessage({ id: 'send' }),
            fullWidth: true,
            isLoading: isPending,
            disabled: !isValid || !email
          }
        ]}
        style={{ zIndex: 2 }}
      >
        <TextInputComponent
          label={formatMessage({ id: 'email' })}
          type="email"
          clearButton={true}
          hintShow={!!errors.email}
          hintVariant={textInputHintVariantOptions.ERROR}
          hintMessage={errors.email?.message}
          fullWidth={true}
          {...register('email')}
        />
      </AlertComponent>
    </form>
  )
}

export default ResetPassword
